import * as React from "react"
import * as AccordionPrimitive from "@radix-ui/react-accordion"
import { CaretDown } from "@phosphor-icons/react"
import * as SwitchPrimitives from "@radix-ui/react-switch"
import { cn } from "@/lib/utils"
import { switchVariants } from "./switch"

const Accordion = AccordionPrimitive.Root

const AccordionItem = ({
  ref,
  className,
  ...props
}: React.ComponentPropsWithRef<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn(`border-b`, className)}
    {...props}
  />
)
AccordionItem.displayName = `AccordionItem`

const AccordionTrigger = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof AccordionPrimitive.Trigger>) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        `flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180`,
        className
      )}
      {...props}
    >
      {children}
      <CaretDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
)
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

const AccordionContent = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof AccordionPrimitive.Content>) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn(`pb-4 pt-0`, className)}>{children}</div>
  </AccordionPrimitive.Content>
)

AccordionContent.displayName = AccordionPrimitive.Content.displayName

const AccordionTriggerWithSwitch = ({
  ref,
  className,
  children,
  ...props
}: React.ComponentPropsWithRef<typeof AccordionPrimitive.Trigger>) => (
  <AccordionPrimitive.Header className="flex flex-1 items-center justify-between py-4 font-medium transition-all hover:underline">
    {children}
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(`group`, className)}
      {...props}
    >
      <SwitchPrimitives.Root
        className={cn(
          switchVariants({ size: `small` }),
          `shrink-0 group-data-[state=open]:bg-green-300 group-data-[state=closed]:bg-input`
        )}
      >
        <SwitchPrimitives.Thumb
          className={cn(
            `accordion-switch pointer-events-none block rounded-full bg-background shadow-lg ring-0 transition-transform`,
            `h-3 w-3 group-data-[state=open]:translate-x-3 group-data-[state=closed]:translate-x-0`
          )}
        />
      </SwitchPrimitives.Root>
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
)

AccordionTriggerWithSwitch.displayName = AccordionPrimitive.Trigger.displayName

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  AccordionTriggerWithSwitch,
}
